import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path } from 'react-native-svg';
import { COLORS, FONTS } from '../../constants/config';
import { PageContainer, Breadcrumb, KPIDeleteConfirmModal } from '../../components';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { unitGoalsApi, goalsApi, organizationalUnitsApi, kpisApi } from '../../services/apiService';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useRoleNavigation, useUnsavedChangesWarning } from '../../hooks';
import { queryClient, queryKeys } from '../../providers/QueryProvider';
import { extractKpiErrorMessage, validateKpiRequiredFields } from '../../utils/kpiErrors';
import { normalizeArabicNumbers } from '../../utils/rtl';
import { useAutoTranslate } from '../../hooks/useAutoTranslate';
import { StrategicGoalList, OrganizationalUnitList, KPIType, Frequency, Polarity, KPIList } from '../../types';

type RouteProps = RouteProp<MainStackParamList, 'AddUnitGoal'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// KPI Form Interface
// ============================================================

interface KPIFormData {
  id: number;
  name: string;
  weight: string;
  description: string;
  kpiType: KPIType | '';
  polarity: Polarity | '';
  startDate: string;
  unit: string;
  frequency: Frequency | '';
  formula: string;
  targetValue: string;
  worstAcceptableValue: string;
  dataSource: string;
  isExpanded: boolean;
  isExisting?: boolean;  // Flag to identify existing KPIs from API
  apiId?: number;        // Original API ID for existing KPIs
}

// ============================================================
// SVG Icon Components
// ============================================================

const ChevronLeftIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronDownIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 9L12 15L18 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronUpIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 15L12 9L6 15" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const TrashIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 6H5H21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const PlusIcon = ({ size = 16, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19M5 12H19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const DragHandleIcon = ({ size = 20, color = '#9CA3AF' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M8 6H8.01M8 12H8.01M8 18H8.01M16 6H16.01M16 12H16.01M16 18H16.01" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const CalendarIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M8 2V6M16 2V6M3 10H21M5 4H19C20.1046 4 21 4.89543 21 6V20C21 21.1046 20.1046 22 19 22H5C3.89543 22 3 21.1046 3 20V6C3 4.89543 3.89543 4 5 4Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// Dropdown Component
// ============================================================

interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownProps {
  value: string;
  options: DropdownOption[];
  placeholder: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const Dropdown = ({ value, options, placeholder, onChange, disabled }: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const { colors, isDark } = useTheme();
  const buttonRef = useRef<View>(null);
  const selectedOption = options.find(opt => opt.value === value);

  const dropdownTheme = {
    bg: colors.card,
    border: colors.border,
    text: colors.textPrimary,
    placeholder: isDark ? '#6B7280' : '#9CA3AF',
    icon: colors.textMuted,
    disabledBg: isDark ? colors.backgroundAlt : '#F1F5F9',
    menuBg: colors.card,
    menuBorder: colors.border,
    itemActiveBg: isDark ? '#451A1A' : '#FEF2F2',
  };

  const handleOpen = () => {
    if (disabled) return;

    if (!isOpen && buttonRef.current) {
      // Use measureInWindow to get button position
      buttonRef.current.measureInWindow((x, y, width, height) => {
        setPosition({
          top: y + height + 4, // 4px gap below button
          left: x,
          width: width,
        });
      });
    }
    setIsOpen(!isOpen);
  };

  // Close on outside click and scroll
  const menuDomRef = useRef<HTMLDivElement | null>(null);
  const menuCallbackRef = useCallback((node: any) => {
    menuDomRef.current = node as HTMLDivElement | null;
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const handleMouseDown = (e: MouseEvent) => {
      const menuEl = menuDomRef.current;
      if (menuEl && menuEl.contains(e.target as Node)) return;
      setIsOpen(false);
    };

    const handleScroll = (e: Event) => {
      const menuEl = menuDomRef.current;
      if (menuEl && menuEl.contains(e.target as Node)) return;
      setIsOpen(false);
    };

    document.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('scroll', handleScroll, true); // capture phase

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const menu = isOpen ? (
    <View
      ref={menuCallbackRef}
      // @ts-ignore - web style
      onMouseDown={(e: any) => e.stopPropagation()}
      style={{
        position: 'fixed' as const,
        top: position.top,
        left: position.left,
        width: position.width,
        backgroundColor: dropdownTheme.menuBg,
        borderWidth: 1,
        borderColor: dropdownTheme.menuBorder,
        borderRadius: 8,
        maxHeight: 200,
        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
        zIndex: 99999,
        overflow: 'hidden' as const,
      }}
    >
      <ScrollView style={styles.dropdownScroll} nestedScrollEnabled>
        {options.map((option) => (
          <TouchableOpacity
            key={option.value}
            style={[
              styles.dropdownItem,
              { backgroundColor: dropdownTheme.bg },
              value === option.value && { backgroundColor: dropdownTheme.itemActiveBg }
            ]}
            onPress={() => {
              onChange(option.value);
              setIsOpen(false);
            }}
          >
            <Text style={[
              styles.dropdownItemText,
              { color: dropdownTheme.text },
              value === option.value && styles.dropdownItemTextActive
            ]}>
              {option.label}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  ) : null;

  return (
    <View style={[styles.dropdownContainer, isOpen && styles.dropdownContainerOpen]}>
      <TouchableOpacity
        ref={buttonRef}
        style={[
          styles.dropdownButton,
          { backgroundColor: dropdownTheme.bg, borderColor: dropdownTheme.border },
          disabled && { backgroundColor: dropdownTheme.disabledBg },
          isOpen && styles.dropdownButtonOpen
        ]}
        onPress={handleOpen}
        disabled={disabled}
      >
        <Text style={[
          styles.dropdownButtonText,
          { color: dropdownTheme.text },
          !selectedOption && { color: dropdownTheme.placeholder }
        ]}>
          {selectedOption?.label || placeholder}
        </Text>
        <ChevronDownIcon size={16} color={dropdownTheme.icon} />
      </TouchableOpacity>
      {menu && ReactDOM.createPortal(menu, document.body)}
    </View>
  );
};

// ============================================================
// Main Component
// ============================================================

export default function AddUnitGoalScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const { unitGoalId, strategicGoalId, unitId, scrollToKpi } = route.params || {};
  const { showSuccess, showError } = useNotification();
  const { navigateToHome, isViewOnly } = useRoleNavigation();
  const { user } = useAuth();
  // Unit goals are operational items a Section Head (رئيس قسم) proposes, so
  // creating them is governed by executive_items.create — NOT the coarse
  // isViewOnly flag (which is true for a Section Head who also holds
  // strategic_goals.create). Without this they'd be bounced on open.
  const canCreateOperationalItem = !!(
    user?.is_superuser
    || (user?.permissions || []).includes('hrcpms.executive_items.create')
  );
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();
  const { translate, translateUnit } = useAutoTranslate();

  // KPI Options - defined inside component to use translations
  const kpiTypeOptions: DropdownOption[] = [
    // 'strategic' omitted — it requires data_source_type (مستقل/تجميعي, DTS
    // 4.3), collected only on AddGoalScreen. Offering it on an operational
    // screen 400s the KPI create and silently drops it on save.
    { value: 'operational', label: t('kpis.operational') },
    { value: 'efficiency', label: t('kpis.efficiency') },
    { value: 'effectiveness', label: t('kpis.effectiveness') },
    { value: 'impact', label: t('kpis.impact') },
  ];

  const polarityOptions: DropdownOption[] = [
    { value: 'positive', label: t('kpis.ascending') },
    { value: 'negative', label: t('kpis.descending') },
  ];

  const unitOptions: DropdownOption[] = [
    { value: '%', label: t('kpis.percentage') },
    { value: 'number', label: t('kpis.number') },
    { value: 'currency', label: t('kpis.currency') },
  ];

  const frequencyOptions: DropdownOption[] = [
    { value: 'daily', label: t('kpis.daily') },
    { value: 'weekly', label: t('kpis.weekly') },
    { value: 'monthly', label: t('kpis.monthly') },
    { value: 'quarterly', label: t('kpis.quarterly') },
    { value: 'half_yearly', label: t('kpis.semiAnnual') },
    { value: 'yearly', label: t('kpis.annual') },
  ];

  // Theme colors for form elements
  const themeColors = {
    mainCard: colors.card,
    mainCardBorder: colors.cardBorder,
    pageTitle: colors.textPrimary,
    section: isDark ? colors.backgroundAlt : '#F8FAFC',
    sectionTitle: colors.textPrimary,
    label: colors.textPrimary,
    input: colors.card,
    inputBorder: colors.border,
    inputText: colors.textPrimary,
    placeholder: isDark ? '#6B7280' : '#9CA3AF',
    iconColor: colors.textMuted,
    kpiCard: colors.card,
    kpiCardBorder: colors.cardBorder,
    kpiCardHeader: colors.card,
    kpiNumber: isDark ? colors.backgroundAlt : '#F1F5F9',
    cancelButton: colors.card,
    cancelButtonBorder: colors.border,
    cancelButtonText: colors.textMuted,
  };

  // Redirect view-only users (executive) - they cannot add/edit
  useEffect(() => {
    if (isViewOnly && !canCreateOperationalItem) {
      navigation.goBack();
    }
  }, [isViewOnly, canCreateOperationalItem, navigation]);

  // Refs for scrolling to KPI section
  const scrollViewRef = useRef<ScrollView>(null);
  const kpiSectionY = useRef<number>(0);

  // Edit mode flag
  const isEditMode = !!unitGoalId;

  // Form state - pre-select unit if unitId is provided
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    strategicGoal: strategicGoalId ? strategicGoalId.toString() : '',
    unit: unitId ? unitId.toString() : '',
    weight: '30',
  });

  // KPIs state - initialize empty, only load from API in edit mode
  const [kpis, setKpis] = useState<KPIFormData[]>([]);
  const [deletedKpiIds, setDeletedKpiIds] = useState<number[]>([]);
  const [kpiPendingDelete, setKpiPendingDelete] = useState<{ localId: number; backendId: number | null; name: string } | null>(null);
  const [nextKpiId, setNextKpiId] = useState(1);
  const [existingKpisLoading, setExistingKpisLoading] = useState(false);


  const [loading, setLoading] = useState(false);
  const [goalsLoading, setGoalsLoading] = useState(true);
  const [unitsLoading, setUnitsLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [goals, setGoals] = useState<StrategicGoalList[]>([]);
  const [units, setUnits] = useState<OrganizationalUnitList[]>([]);
  const [kpiSectionExpanded, setKpiSectionExpanded] = useState(true);

  useUnsavedChangesWarning(!!formData.name || !!formData.description);

  // Fetch goals and units for dropdowns
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [goalsResponse, unitsResponse] = await Promise.all([
          goalsApi.list({ page_size: 100 }),
          organizationalUnitsApi.list({ page_size: 9999 }),
        ]);
        setGoals(goalsResponse.results || []);
        setUnits(unitsResponse.results || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setGoalsLoading(false);
        setUnitsLoading(false);
      }
    };
    fetchData();
  }, []);

  // Fetch unit goal data when in edit mode
  useEffect(() => {
    const fetchUnitGoal = async () => {
      if (!unitGoalId) return;

      try {
        setInitialLoading(true);
        const unitGoal = await unitGoalsApi.get(unitGoalId);

        // Populate form with unit goal data
        setFormData({
          name: unitGoal.name || '',
          description: unitGoal.description || '',
          strategicGoal: unitGoal.strategic_goal?.toString() || '',
          unit: unitGoal.unit?.toString() || '',
          weight: unitGoal.weight?.toString() || '30',
        });

        // Fetch KPIs linked directly to this unit goal and convert to editable form data
        setExistingKpisLoading(true);
        try {
          const kpisData = await kpisApi.list({ organizational_unit_goal: unitGoalId, page_size: 100 });

          // Convert API KPIs to KPIFormData format for editing
          const existingKpiData: KPIFormData[] = (kpisData.results || []).map((kpi: KPIList, index: number) => ({
            id: -(index + 1), // Use negative IDs for existing KPIs to avoid conflicts
            name: kpi.name,
            weight: (kpi as any).weight?.toString() || '30',
            description: kpi.description || '',
            kpiType: (kpi.kpi_type || 'operational') as KPIType,
            polarity: (kpi as any).polarity || 'positive',
            startDate: (kpi as any).measurement_start_date || '',
            unit: kpi.unit || '',
            frequency: (kpi.frequency || 'monthly') as Frequency,
            formula: (kpi as any).measurement_formula || '',
            targetValue: kpi.target_value || '0',
            worstAcceptableValue: (kpi as any).worst_acceptable_value != null ? String((kpi as any).worst_acceptable_value) : '',
            dataSource: (kpi as any).data_source || '',
            isExpanded: false,
            isExisting: true,
            apiId: kpi.id,
          }));

          setKpis(existingKpiData);
          setNextKpiId(existingKpiData.length + 1);
        } catch (err) {
          console.error('Error fetching KPIs:', err);
        } finally {
          setExistingKpisLoading(false);
        }
      } catch (error) {
        console.error('Error fetching unit goal:', error);
        showError(t('employees.loadFailed'));
      } finally {
        setInitialLoading(false);
      }
    };

    fetchUnitGoal();
  }, [unitGoalId]);

  // Scroll to KPI section function
  const scrollToKpiSection = useCallback(() => {
    if (scrollToKpi && kpiSectionY.current > 0) {
      setTimeout(() => {
        scrollViewRef.current?.scrollTo({ y: kpiSectionY.current - 100, animated: true });
      }, 100);
    }
  }, [scrollToKpi]);

  // Scroll to KPI section when data is loaded
  useEffect(() => {
    if (!initialLoading && scrollToKpi) {
      scrollToKpiSection();
    }
  }, [initialLoading, scrollToKpi, scrollToKpiSection]);

  const goalOptions: DropdownOption[] = goals.map(goal => ({
    value: goal.id.toString(),
    label: translate(goal.title),
  }));

  const unitOptionsDropdown: DropdownOption[] = units.map(unit => ({
    value: unit.id.toString(),
    label: translateUnit(unit.name_ar || unit.name),
  }));

  // Update form field
  const updateField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Add new KPI
  const handleAddKPI = () => {
    const newKpi: KPIFormData = {
      id: nextKpiId,
      name: '',
      weight: '30',
      description: '',
      kpiType: 'operational',
      polarity: '',
      startDate: '',
      unit: '',
      frequency: '',
      formula: '',
      targetValue: '0.0',
      worstAcceptableValue: '',
      dataSource: '',
      isExpanded: true,
    };
    setKpis([...kpis, newKpi]);
    setNextKpiId(nextKpiId + 1);
  };

  // Update KPI field
  const updateKPI = (id: number, data: Partial<KPIFormData>) => {
    setKpis(kpis.map(kpi => kpi.id === id ? { ...kpi, ...data } : kpi));
  };

  // Remove KPI — open confirmation first
  const deleteKPI = (id: number) => {
    const kpiToRemove = kpis.find(kpi => kpi.id === id);
    const backendId = kpiToRemove?.isExisting && kpiToRemove.apiId ? kpiToRemove.apiId : null;
    setKpiPendingDelete({
      localId: id,
      backendId,
      name: (kpiToRemove as any)?.name || '',
    });
  };

  const confirmDeleteKPI = () => {
    if (!kpiPendingDelete) return;
    const { localId, backendId } = kpiPendingDelete;
    if (backendId !== null) {
      setDeletedKpiIds(prev => [...prev, backendId]);
    }
    setKpis(prev => prev.filter(kpi => kpi.id !== localId));
    setKpiPendingDelete(null);
  };

  // Toggle KPI expand/collapse
  const toggleKPIExpand = (id: number) => {
    setKpis(kpis.map(kpi => kpi.id === id ? { ...kpi, isExpanded: !kpi.isExpanded } : kpi));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('unitGoals.goalNameRequired');
    }
    if (!formData.unit) {
      newErrors.unit = t('unitGoals.unitRequired');
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return false;

    const kpiError = validateKpiRequiredFields(kpis, t);
    if (kpiError) { showError(kpiError); return false; }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showError(t('form.required'));
      return;
    }

    setLoading(true);
    try {
      const unitGoalData = {
        name: formData.name,
        description: formData.description,
        strategic_goal: formData.strategicGoal ? parseInt(formData.strategicGoal) : null,
        unit: parseInt(formData.unit),
        weight: formData.weight,
      };

      let savedUnitGoalId: number;

      if (isEditMode && unitGoalId) {
        await unitGoalsApi.update(unitGoalId, unitGoalData);
        savedUnitGoalId = unitGoalId;
      } else {
        const response = await unitGoalsApi.create(unitGoalData);
        savedUnitGoalId = response.id;
      }

      // Delete removed KPIs from backend
      let kpiDeleteFailed = 0;
      if (deletedKpiIds.length > 0) {
        for (const kpiId of deletedKpiIds) {
          try {
            await kpisApi.delete(kpiId);
          } catch (deleteError: any) {
            kpiDeleteFailed++;
          }
        }
      }
      if (kpiDeleteFailed > 0) {
        showError(t('kpis.deleteFailed'));
      }

      // Handle KPIs - update existing ones and create new ones
      const kpiErrors: string[] = [];
      for (const kpi of kpis) {
        if (!kpi.name) continue;
        const kpiData = {
          name: kpi.name,
          description: kpi.description || '',
          kpi_type: kpi.kpiType || 'operational',
          unit: kpi.unit || '%',
          target_value: (parseFloat(kpi.targetValue) || 0).toFixed(2),
          worst_acceptable_value:
            kpi.polarity === 'negative' && kpi.unit !== '%' && kpi.worstAcceptableValue !== ''
              ? (parseFloat(kpi.worstAcceptableValue) || 0).toFixed(2)
              : null,
          frequency: kpi.frequency || 'monthly',
          polarity: kpi.polarity || 'positive',
          weight: kpi.weight || '30',
          weight_from_source: '30',
          achieved_value: '0',
          source_type: 'unit_goal',
          evidence_required: false,
          measurement_start_date: kpi.startDate || null,
          measurement_formula: kpi.formula || '',
          data_source: kpi.dataSource || '',
          owner: kpi.dataSource || t('common.notSpecified'),
          organizational_unit_goal: savedUnitGoalId,
        };

        if (kpi.isExisting && kpi.apiId) {
          try {
            await kpisApi.update(kpi.apiId, kpiData);
          } catch (updateError: any) {
            const errMsg = extractKpiErrorMessage(updateError?.response?.data);
            kpiErrors.push(t('kpis.kpiUpdateError', { name: kpi.name, error: errMsg }));
            console.warn(`KPI ${kpi.apiId} update failed:`, updateError?.response?.data);
          }
        } else {
          try {
            await kpisApi.create(kpiData);
          } catch (createError: any) {
            const errMsg = extractKpiErrorMessage(createError?.response?.data);
            kpiErrors.push(t('kpis.kpiSaveError', { name: kpi.name, error: errMsg }));
            console.warn(`KPI create failed:`, createError?.response?.data);
          }
        }
      }

      if (kpiErrors.length > 0) {
        showError(kpiErrors.join('\n'));
      }

      showSuccess(isEditMode ? t('unitGoals.updateSuccess') : t('unitGoals.createSuccess'));
      queryClient.invalidateQueries({ queryKey: queryKeys.unitGoals.all });
      navigation.goBack();
    } catch (error: any) {
      console.error('Error saving unit goal:', error);
      console.error('Error response data:', error.response?.data);
      let errorMsg = t('unitGoals.saveFailed');
      const data = error.response?.data;
      if (data) {
        if (typeof data === 'string') {
          errorMsg = data;
        } else if (data.detail) {
          errorMsg = data.detail;
        } else if (typeof data === 'object') {
          // Extract field-level validation errors (e.g. {"name": ["error msg"]})
          const messages = Object.values(data)
            .flat()
            .filter((v): v is string => typeof v === 'string');
          if (messages.length > 0) {
            errorMsg = messages.join('\n');
          }
        }
      }
      showError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Render KPI Card
  const renderKPICard = (kpi: KPIFormData, index: number) => (
    <View key={kpi.id} style={[styles.kpiCard, { backgroundColor: themeColors.kpiCard, borderColor: themeColors.kpiCardBorder }]}>
      {/* KPI Header */}
      <View style={[styles.kpiCardHeader, { backgroundColor: themeColors.kpiCardHeader, borderBottomColor: themeColors.kpiCardBorder }]}>
        <View style={styles.kpiCardHeaderLeft}>
          <TouchableOpacity
            style={styles.kpiHeaderButton}
            onPress={() => toggleKPIExpand(kpi.id)}
          >
            {kpi.isExpanded ? (
              <ChevronUpIcon size={20} color={themeColors.iconColor} />
            ) : (
              <ChevronDownIcon size={20} color={themeColors.iconColor} />
            )}
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.kpiHeaderButton}
            onPress={() => deleteKPI(kpi.id)}
          >
            <TrashIcon size={18} color={themeColors.iconColor} />
          </TouchableOpacity>
        </View>
        <View style={styles.kpiCardHeaderRight}>
          <Text style={[styles.kpiCardName, { color: themeColors.inputText }]}>
            {kpi.name || `${t('kpis.kpiName')} ${index + 1}`}
          </Text>
          <View style={[styles.kpiCardNumber, { backgroundColor: themeColors.kpiNumber }]}>
            <Text style={[styles.kpiCardNumberText, { color: themeColors.inputText }]}>{index + 1}</Text>
          </View>
          <DragHandleIcon size={20} color={themeColors.iconColor} />
        </View>
      </View>

      {/* KPI Content - Collapsible */}
      {kpi.isExpanded && (
        <View style={styles.kpiCardContent}>
          {/* Basic Information Subsection */}
          <View style={[styles.subsection, { backgroundColor: themeColors.section }]}>
            <Text style={[styles.subsectionTitle, { color: themeColors.sectionTitle }]}>{t('kpis.basicInformation')}</Text>

            <View style={styles.row}>
              <View style={styles.fieldSmall}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('common.weight')}</Text>
                </View>
                <View style={styles.weightInputRow}>
                  <TouchableOpacity style={[styles.setWeightButton, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                    <Text style={[styles.setWeightButtonText, { color: themeColors.inputText }]}>{t('kpis.setWeight')}</Text>
                  </TouchableOpacity>
                  <Text style={[styles.percentSymbol, { color: themeColors.iconColor }]}>%</Text>
                  <TextInput
                    style={[styles.weightInput, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                    value={kpi.weight}
                    onChangeText={(v) => updateKPI(kpi.id, { weight: normalizeArabicNumbers(v) })}
                    keyboardType="numeric"
                    textAlign="right"
                  />
                </View>
              </View>

              <View style={styles.fieldLarge}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.kpiName')}</Text>
                </View>
                <TextInput
                  style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                  value={kpi.name}
                  onChangeText={(v) => updateKPI(kpi.id, { name: v })}
                  placeholder=""
                  textAlign="right"
                />
              </View>
            </View>

            <View style={styles.field}>
              <View style={styles.labelRow}>
                <Text style={styles.requiredStar}>*</Text>
                <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.description')}</Text>
              </View>
              <TextInput
                style={[styles.input, styles.textArea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                value={kpi.description}
                onChangeText={(v) => updateKPI(kpi.id, { description: v })}
                placeholder=""
                textAlign="right"
                multiline
                numberOfLines={3}
              />
            </View>
          </View>

          {/* Classification Subsection */}
          <View style={[styles.subsection, { zIndex: 30, backgroundColor: themeColors.section }]}>
            <Text style={[styles.subsectionTitle, { color: themeColors.sectionTitle }]}>{t('kpis.classification')}</Text>

            <View style={[styles.row, { zIndex: 10 }]}>
              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.polarity')}</Text>
                </View>
                <Dropdown
                  value={kpi.polarity}
                  options={polarityOptions}
                  placeholder={t('kpis.selectPolarityHint')}
                  onChange={(v) => updateKPI(kpi.id, { polarity: v as Polarity })}
                />
              </View>

              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.kpiClassification')}</Text>
                </View>
                <Dropdown
                  value={kpi.kpiType}
                  options={kpiTypeOptions}
                  placeholder={t('kpis.selectClassification')}
                  onChange={(v) => updateKPI(kpi.id, { kpiType: v as KPIType })}
                />
              </View>
            </View>
          </View>

          {/* Measurement Subsection */}
          <View style={[styles.subsection, { zIndex: 20, backgroundColor: themeColors.section }]}>
            <Text style={[styles.subsectionTitle, { color: themeColors.sectionTitle }]}>{t('kpis.measurement')}</Text>

            {/* Start Date */}
            <View style={styles.field}>
              <View style={styles.labelRow}>
                <Text style={styles.requiredStar}>*</Text>
                <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.measurementStartDate')}</Text>
              </View>
              <View style={[styles.dateInputContainer, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                {Platform.OS === 'web' ? (
                  <input
                    type="date"
                    value={kpi.startDate}
                    onChange={(e: any) => updateKPI(kpi.id, { startDate: e.target.value })}
                    style={{
                      flex: 1,
                      border: 'none',
                      outline: 'none',
                      backgroundColor: 'transparent',
                      fontSize: 14,
                      fontFamily: 'inherit',
                      color: isDark ? '#F9FAFB' : '#414042',
                      textAlign: 'right' as const,
                      direction: 'rtl' as const,
                      padding: '10px 0',
                      cursor: 'pointer',
                    }}
                  />
                ) : (
                  <>
                    <CalendarIcon size={16} color={themeColors.iconColor} />
                    <TextInput
                      style={[styles.dateInput, { color: themeColors.inputText }]}
                      value={kpi.startDate}
                      onChangeText={(v) => updateKPI(kpi.id, { startDate: v })}
                      placeholder="YYYY-MM-DD"
                      placeholderTextColor={themeColors.placeholder}
                      textAlign="right"
                    />
                  </>
                )}
              </View>
            </View>

            {/* Unit & Frequency */}
            <View style={[styles.row, { zIndex: 10 }]}>
              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.measurementFrequency')}</Text>
                </View>
                <Dropdown
                  value={kpi.frequency}
                  options={frequencyOptions}
                  placeholder={t('kpis.selectFrequency')}
                  onChange={(v) => updateKPI(kpi.id, { frequency: v as Frequency })}
                />
              </View>

              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.measurementUnit')}</Text>
                </View>
                <Dropdown
                  value={kpi.unit}
                  options={unitOptions}
                  placeholder={t('kpis.selectUnitHint')}
                  onChange={(v) => updateKPI(kpi.id, { unit: v })}
                />
              </View>
            </View>

            {/* Formula */}
            <View style={styles.field}>
              <View style={styles.labelRow}>
                <Text style={styles.requiredStar}>*</Text>
                <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.measurementFormula')}</Text>
              </View>
              <TextInput
                style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                value={kpi.formula}
                onChangeText={(v) => updateKPI(kpi.id, { formula: v })}
                placeholder={t('kpis.formulaPlaceholder')}
                placeholderTextColor={themeColors.placeholder}
                textAlign="right"
              />
            </View>

            {/* Target Value & Data Source */}
            <View style={styles.row}>
              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.dataSource')}</Text>
                </View>
                <TextInput
                  style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                  value={kpi.dataSource}
                  onChangeText={(v) => updateKPI(kpi.id, { dataSource: v })}
                  placeholder={t('kpis.dataSourcePlaceholder')}
                  placeholderTextColor={themeColors.placeholder}
                  textAlign="right"
                />
              </View>

              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.targetValue')}</Text>
                </View>
                <TextInput
                  style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                  value={kpi.targetValue}
                  onChangeText={(v) => updateKPI(kpi.id, { targetValue: normalizeArabicNumbers(v) })}
                  placeholder="0.0"
                  placeholderTextColor={themeColors.placeholder}
                  keyboardType="numeric"
                  textAlign="right"
                />
              </View>
            </View>

            {/* Worst Acceptable Value — only for negative polarity + non-% unit */}
            {kpi.polarity === 'negative' && kpi.unit && kpi.unit !== '%' && (
              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  {parseFloat(kpi.targetValue) === 0 && (
                    <Text style={styles.requiredStar}>*</Text>
                  )}
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.worstAcceptableValue')}</Text>
                </View>
                <TextInput
                  style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                  value={kpi.worstAcceptableValue}
                  onChangeText={(v) => updateKPI(kpi.id, { worstAcceptableValue: normalizeArabicNumbers(v) })}
                  placeholder={t('kpis.worstAcceptableValueHint')}
                  placeholderTextColor={themeColors.placeholder}
                  keyboardType="numeric"
                  textAlign="right"
                />
              </View>
            )}
          </View>
        </View>
      )}
    </View>
  );

  // Show loading while fetching data in edit mode
  if (initialLoading) {
    return (
      <PageContainer>
        <View style={styles.loadingFullContainer}>
          <ActivityIndicator size="large" color="#A9211F" />
          <Text style={[styles.loadingText, { color: isDark ? '#9CA3AF' : '#6B7280' }]}>{t('common.loadingData')}</Text>
        </View>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ScrollView ref={scrollViewRef} style={styles.scrollView} contentContainerStyle={styles.content}>
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: t('navigation.home'), route: 'Dashboard' },
            { label: t('unitGoals.title'), route: 'UnitGoals' },
            { label: isEditMode ? t('unitGoals.editGoal') : t('unitGoals.addNewGoal') },
          ]}
        />

        {/* Page Title */}
        <Text style={[styles.pageTitle, { color: themeColors.pageTitle }]}>{isEditMode ? t('unitGoals.editGoal') : t('unitGoals.addNewGoal')}</Text>

        {/* Main Card Container */}
        <View style={[styles.mainCard, { backgroundColor: themeColors.mainCard, borderColor: themeColors.mainCardBorder, overflow: 'visible', zIndex: 10 }]}>
          {/* Basic Information Section */}
          <View style={[styles.section, { zIndex: 30, overflow: 'visible', backgroundColor: themeColors.section, borderColor: themeColors.inputBorder }]}>
            <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>{t('kpis.basicInformation')}</Text>

            {/* Row 1: Name + Weight */}
            <View style={styles.row}>
              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('common.weight')}</Text>
                </View>
                <View style={styles.weightInputRow}>
                  <TouchableOpacity style={[styles.setWeightButton, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                    <Text style={[styles.setWeightButtonText, { color: themeColors.inputText }]}>{t('kpis.setWeight')}</Text>
                  </TouchableOpacity>
                  <Text style={[styles.percentSymbol, { color: themeColors.iconColor }]}>%</Text>
                  <TextInput
                    style={[styles.weightInput, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                    value={formData.weight}
                    onChangeText={(v) => updateField('weight', normalizeArabicNumbers(v))}
                    keyboardType="numeric"
                    textAlign="right"
                  />
                </View>
              </View>

              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('common.name')}</Text>
                </View>
                <TextInput
                  style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }, errors.name ? styles.inputError : undefined]}
                  value={formData.name}
                  onChangeText={(v) => updateField('name', v)}
                  placeholder={t('unitGoals.enterGoalName')}
                  placeholderTextColor={themeColors.placeholder}
                  textAlign="right"
                />
                {errors.name && <Text style={styles.errorText}>{errors.name}</Text>}
              </View>
            </View>

            {/* Row 2: Description - Full width */}
            <View style={styles.field}>
              <View style={styles.labelRow}>
                <Text style={[styles.label, { color: themeColors.label }]}>{t('common.description')}</Text>
              </View>
              <TextInput
                style={[styles.input, styles.textArea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                value={formData.description}
                onChangeText={(v) => updateField('description', v)}
                placeholder=""
                placeholderTextColor={themeColors.placeholder}
                textAlign="right"
                multiline
                numberOfLines={3}
              />
            </View>

            {/* Row 3: Strategic Goal + Unit */}
            <View style={[styles.row, { zIndex: 20, overflow: 'visible' }]}>
              <View style={[styles.fieldHalf, { zIndex: 10, overflow: 'visible' }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('unitGoals.responsibleUnit')}</Text>
                </View>
                {unitsLoading ? (
                  <View style={[styles.loadingContainer, { backgroundColor: isDark ? themeColors.section : '#F3F4F6' }]}>
                    <ActivityIndicator size="small" color="#A9211F" />
                  </View>
                ) : (
                  <Dropdown
                    value={formData.unit}
                    options={unitOptionsDropdown}
                    placeholder={t('unitGoals.selectResponsible')}
                    onChange={(v) => updateField('unit', v)}
                  />
                )}
                {errors.unit && <Text style={styles.errorText}>{errors.unit}</Text>}
              </View>

              <View style={[styles.fieldHalf, { zIndex: 20, overflow: 'visible' }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('goals.strategicGoal')}</Text>
                </View>
                {goalsLoading ? (
                  <View style={[styles.loadingContainer, { backgroundColor: isDark ? themeColors.section : '#F3F4F6' }]}>
                    <ActivityIndicator size="small" color="#A9211F" />
                  </View>
                ) : (
                  <Dropdown
                    value={formData.strategicGoal}
                    options={goalOptions}
                    placeholder={t('goals.selectStrategicGoal')}
                    onChange={(v) => updateField('strategicGoal', v)}
                  />
                )}
              </View>
            </View>
          </View>
        </View>

        {/* KPIs Section */}
        <View style={[styles.mainCard, { backgroundColor: themeColors.mainCard, borderColor: themeColors.mainCardBorder, overflow: 'visible', zIndex: 1 }]}>
          <View
            style={[styles.kpiSectionWrapper, { backgroundColor: themeColors.section }]}
            onLayout={(event) => {
              kpiSectionY.current = event.nativeEvent.layout.y;
            }}
          >
            {/* Section Header */}
            <TouchableOpacity
              style={styles.kpiSectionHeader}
              onPress={() => setKpiSectionExpanded(!kpiSectionExpanded)}
            >
              {kpiSectionExpanded ? (
                <ChevronUpIcon size={20} color={themeColors.iconColor} />
              ) : (
                <ChevronDownIcon size={20} color={themeColors.iconColor} />
              )}
              <Text style={[styles.kpiSectionTitle, { color: themeColors.sectionTitle }]}>{t('kpis.performanceIndicators')}</Text>
            </TouchableOpacity>

            {kpiSectionExpanded && (
              <View style={styles.kpiSectionContent}>
                {/* Loading indicator for existing KPIs */}
                {existingKpisLoading ? (
                  <View style={styles.existingKpisLoading}>
                    <ActivityIndicator size="small" color={COLORS.primary} />
                    <Text style={[styles.existingKpisLoadingText, { color: themeColors.iconColor }]}>{t('kpis.loadingKPIs')}</Text>
                  </View>
                ) : (
                  <>
                    {/* Editable KPI Cards */}
                    {kpis.map((kpi, index) => renderKPICard(kpi, index))}

                    {/* Add KPI Button */}
                    <TouchableOpacity style={[styles.addKpiButton, { borderColor: themeColors.inputBorder }]} onPress={handleAddKPI}>
                      <Text style={[styles.addKpiButtonText, { color: themeColors.inputText }]}>{t('kpis.addPerformanceIndicator')}</Text>
                      <PlusIcon size={16} color={themeColors.inputText} />
                    </TouchableOpacity>
                  </>
                )}
              </View>
            )}
          </View>
        </View>

        {/* Footer Buttons */}
        <View style={styles.footer}>
          <TouchableOpacity
            style={styles.saveButton}
            onPress={handleSave}
            disabled={loading}
          >
            {loading ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <Text style={styles.saveButtonText}>{t('common.save')}</Text>
            )}
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.cancelButton, { backgroundColor: themeColors.cancelButton, borderColor: themeColors.cancelButtonBorder }]}
            onPress={() => navigation.goBack()}
          >
            <Text style={[styles.cancelButtonText, { color: themeColors.cancelButtonText }]}>{t('common.cancel')}</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
      <KPIDeleteConfirmModal
        visible={kpiPendingDelete !== null}
        kpiId={kpiPendingDelete?.backendId ?? null}
        kpiName={kpiPendingDelete?.name}
        onConfirm={confirmDeleteKPI}
        onCancel={() => setKpiPendingDelete(null)}
      />
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 24,
    paddingBottom: 40,
  },
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    marginBottom: 16,
  },
  breadcrumbLink: {
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: '#9CA3AF',
  },
  breadcrumbActive: {
    fontFamily: FONTS.medium,
    fontSize: 14,
    color: COLORS.primary,
  },
  pageTitle: {
    fontFamily: FONTS.medium,
    fontSize: 20,
    color: '#414042',
    textAlign: 'right',
    marginBottom: 24,
    fontWeight: '700',
  },
  mainCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    padding: 24,
    marginBottom: 24,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontFamily: FONTS.medium,
    fontSize: 16,
    color: '#1F2937',
    textAlign: 'right',
    marginBottom: 16,
    fontWeight: '600',
  },
  row: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 16,
  },
  field: {
    marginBottom: 16,
  },
  fieldHalf: {
    flex: 1,
  },
  labelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 4,
    marginBottom: 8,
  },
  label: {
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: '#374151',
  },
  requiredStar: {
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: '#EF4444',
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: '#414042',
    textAlign: 'right',
  },
  inputError: {
    borderColor: '#EF4444',
  },
  textArea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  errorText: {
    fontFamily: FONTS.regular,
    fontSize: 12,
    color: '#EF4444',
    textAlign: 'right',
    marginTop: 4,
  },
  weightInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  weightInput: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: '#414042',
    textAlign: 'right',
  },
  percentSymbol: {
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: '#6B7280',
  },
  setWeightButton: {
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  setWeightButtonText: {
    fontFamily: FONTS.regular,
    fontSize: 12,
    color: '#64748B',
  },
  dropdownContainer: {
    position: 'relative',
    zIndex: 1,
  },
  dropdownContainerOpen: {
    zIndex: 9999,
  },
  dropdownButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    minHeight: 44,
  },
  dropdownButtonOpen: {
    borderColor: '#A9211F',
  },
  dropdownDisabled: {
    backgroundColor: '#F3F4F6',
  },
  dropdownButtonText: {
    flex: 1,
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: '#414042',
    textAlign: 'right',
  },
  dropdownPlaceholder: {
    color: '#9CA3AF',
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    marginTop: 4,
    maxHeight: 200,
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
    elevation: 20,
    zIndex: 99999,
    overflow: 'hidden',
  },
  dropdownScroll: {
    maxHeight: 200,
  },
  dropdownItem: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#FFFFFF',
  },
  dropdownItemActive: {
    backgroundColor: '#FEF2F2',
  },
  dropdownItemText: {
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: '#414042',
    textAlign: 'right',
  },
  dropdownItemTextActive: {
    color: '#A9211F',
    fontWeight: '600',
  },
  loadingContainer: {
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F3F4F6',
    borderRadius: 8,
  },
  loadingFullContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
  },
  loadingText: {
    fontFamily: FONTS.regular,
    fontSize: 16,
    color: '#6B7280',
  },
  // KPI Section (inside mainCard)
  kpiSectionWrapper: {
    backgroundColor: '#F8FAFC',
    borderRadius: 8,
  },
  kpiSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    paddingHorizontal: 16,
    gap: 8,
    minHeight: 44,
  },
  kpiSectionTitle: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'center',
  },
  kpiSectionContent: {
    padding: 16,
    paddingTop: 0,
  },
  // KPI Card
  kpiCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    marginBottom: 12,
  },
  kpiCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 12,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  kpiCardHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  kpiCardHeaderRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  kpiHeaderButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  kpiCardNumber: {
    width: 28,
    height: 28,
    borderRadius: 6,
    backgroundColor: '#F1F5F9',
    alignItems: 'center',
    justifyContent: 'center',
  },
  kpiCardNumberText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
  },
  kpiCardName: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  kpiCardContent: {
    padding: 16,
  },
  // Subsection (inside KPI card content)
  subsection: {
    backgroundColor: '#F8FAFC',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
  },
  subsectionTitle: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'center',
    marginBottom: 16,
  },
  fieldSmall: {
    flex: 1,
  },
  fieldLarge: {
    flex: 2,
  },
  dateInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
  },
  dateInput: {
    flex: 1,
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: '#414042',
    paddingVertical: 10,
  },
  addKpiButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    borderStyle: 'dashed',
    backgroundColor: '#FFFFFF',
    minHeight: 44,
  },
  addKpiButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
  footer: {
    flexDirection: 'row',
    gap: 12,
  },
  saveButton: {
    backgroundColor: '#A9211F',
    borderRadius: 8,
    paddingHorizontal: 24,
    paddingVertical: 10,
    minWidth: 80,
    alignItems: 'center',
  },
  saveButtonText: {
    fontFamily: FONTS.medium,
    fontSize: 14,
    color: '#FFFFFF',
  },
  cancelButton: {
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    paddingHorizontal: 24,
    paddingVertical: 10,
  },
  cancelButtonText: {
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: '#64748B',
  },
  // Existing KPIs styles - Matching Figma design
  existingKpisSection: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 20,
    padding: 12,
  },
  existingKpisLoading: {
    flex: 1,
    padding: 24,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 8,
  },
  existingKpisLoadingText: {
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: '#64748B',
  },
  kpiCardsContainer: {
    padding: 16,
    paddingTop: 0,
  },
  existingKpisEmpty: {
    flex: 1,
    padding: 24,
    alignItems: 'center',
  },
  existingKpisEmptyText: {
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: '#9CA3AF',
  },
  existingKpisList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 20,
    width: '100%',
  },
  existingKpiCard: {
    flex: 1,
    minWidth: 250,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    padding: 12,
    gap: 16,
  },
  existingKpiName: {
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 20,
  },
  existingKpiStats: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
  },
  existingKpiValueBadge: {
    backgroundColor: '#F1F5F9',
    borderRadius: 100,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  existingKpiValueText: {
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    fontSize: 14,
    color: '#414042',
    textAlign: 'center',
  },
  existingKpiProgressContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 0,
  },
  existingKpiProgressBar: {
    flex: 1,
    height: 8,
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    overflow: 'visible',
  },
  existingKpiProgressFill: {
    height: '100%',
    borderRadius: 8,
  },
  existingKpiDotOuter: {
    width: 14,
    height: 14,
    borderRadius: 7,
    borderWidth: 2,
    borderColor: '#2CA02C',
    backgroundColor: '#FCFCFC',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: -5,
    zIndex: 2,
  },
  existingKpiDotInner: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#2CA02C',
  },
  existingKpiLink: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    alignSelf: 'flex-start',
    gap: 8,
    paddingVertical: 3,
    minHeight: 24,
  },
  existingKpiLinkText: {
    fontFamily: FONTS.regular,
    fontSize: 12,
    color: '#414042',
    textAlign: 'right',
  },
});
